// Reliable "Print / Save as PDF" helper.
// Opens the HTML in a new window with a proper @page rule so the browser
// print dialog can save to A4 / Legal / Letter etc. — no sidebar bleed-through.

export function printHtml(bodyHtml, { title = "Print", pageSize = "A4" } = {}) {
  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) {
    // Popup blocked — fall back to legacy in-place print
    window.print();
    return;
  }
  w.document.open();
  w.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title.replace(/</g, "&lt;")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
<style>
  @page { size: ${pageSize}; margin: 18mm 16mm 18mm 16mm; }
  * { box-sizing: border-box; font-family: 'Manrope', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; }
  .page { padding: 0; }
  @media screen { .page { max-width: 210mm; margin: 0 auto; padding: 16mm; } }
  table { border-collapse: collapse; width: 100%; }
  table, th, td { border-color: #d4d4d4; }
  thead { display: table-header-group; }
  tr, td, th { page-break-inside: avoid; }
  .letterhead { max-height: 30mm; max-width: 100%; object-fit: contain; }
  .grid { display: grid; }
  .flex { display: flex; }
  .between { justify-content: space-between; }
  .mt-4 { margin-top: 16px; } .mb-4 { margin-bottom: 16px; }
  .mt-6 { margin-top: 24px; } .mt-2 { margin-top: 8px; }
  .pb-4 { padding-bottom: 16px; } .pt-2 { padding-top: 8px; } .pt-4 { padding-top: 16px; }
  .text-right { text-align: right; }
  .text-xs { font-size: 10px; } .text-sm { font-size: 12px; } .text-lg { font-size: 16px; } .text-xl { font-size: 20px; } .text-2xl { font-size: 24px; }
  .font-mono { font-variant-numeric: tabular-nums; }
  .font-bold { font-weight: 700; } .font-semibold { font-weight: 600; }
  .uppercase { text-transform: uppercase; }
  .tracking-wider { letter-spacing: .05em; }
  .text-neutral-500 { color: #737373; } .text-neutral-600 { color: #525252; } .text-neutral-700 { color: #404040; } .text-neutral-900 { color: #171717; }
  .border-b { border-bottom: 1px solid #e5e5e5; } .border-t { border-top: 1px solid #d4d4d4; }
  .whitespace-pre-line { white-space: pre-line; }
  .section { break-inside: avoid; }
  .totals { width: 45%; margin-left: auto; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .totals .grand { border-top: 1px solid #171717; font-size: 15px; font-weight: 700; padding-top: 6px; margin-top: 4px; }
  .items th, .items td { padding: 6px 8px; border-bottom: 1px solid #e5e5e5; font-size: 11px; }
  .items th { background: #fafafa; text-align: left; text-transform: uppercase; letter-spacing: .05em; font-size: 9px; color: #737373; }
  .items td.num, .items th.num { text-align: right; }
  @media print { .no-print { display: none !important; } }
</style>
</head>
<body>
<div class="page">${bodyHtml}</div>
<script>
  // Give the browser a tick to layout the images then trigger print.
  window.addEventListener("load", function () {
    var go = function () { setTimeout(function () { window.focus(); window.print(); }, 250); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(go); else go();
  });
</script>
</body>
</html>`);
  w.document.close();
}

export function currency(v) {
  if (v === undefined || v === null || isNaN(v)) return "\u20B90";
  return "\u20B9" + Number(v).toLocaleString("en-IN");
}

// Indian Financial Year helpers (April 1 - March 31)
export function currentFY() {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  const start = m >= 4 ? y : y - 1;
  return { start, end: start + 1, label: `FY ${start}-${String(start + 1).slice(-2)}` };
}

export function fyRange(startYear) {
  return {
    from: `${startYear}-04-01`,
    to: `${startYear + 1}-03-31`,
    label: `FY ${startYear}-${String(startYear + 1).slice(-2)}`,
    start: startYear,
  };
}

export function listFYs(count = 5) {
  const cur = currentFY().start;
  const out = [];
  for (let i = 0; i < count; i++) out.push(fyRange(cur - i));
  return out;
}

// Share helpers — open user's WhatsApp / mail client with pre-filled content.
export function shareWhatsApp(text, phone = "") {
  const cleanPhone = String(phone || "").replace(/[^0-9]/g, "");
  const url = cleanPhone
    ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`
    : `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function shareEmail({ to = "", subject = "", body = "" } = {}) {
  const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = url;
}
